import ExcelJS from "exceljs";
import moment from "moment";

/**
 * Бланк факта смены для ручного заполнения (XLSX, ExcelJS).
 *
 * Структура листа основной выгрузки (по утверждённому образцу):
 *   Строка 1: «Дата» | дата | день/ночь | «Мастер» | мастер (без переносов)
 *   Строка 2: пустая
 *   Строка 3: шапка таблицы — Линия | N парт | продукт | тара | количество |
 *             факт | карантин | Примечание
 *   Строка 4+: строки факта (отсортированы по номеру партии числом);
 *             колонки «факт», «карантин», «Примечание» — пустые
 *             (заполняются вручную).
 *
 * Выгрузки из списка смен (журналы факта):
 *   «Общий» — все смены на одном листе: зелёная шапка и строки данных
 *   Линия | дата (ДД.ММ.ГГ) | смена д/н | мастер | N парт | продукт | тара |
 *   количество. «Количество» — зарегистрированный факт («Кол-во выпущенного
 *   продукта, шт»); сортировка: дата → линия → смена д/н → номер партии
 *   (числом). Поля страницы: слева 2см, сверху/справа/снизу 0,5см.
 *
 *   «По сменам» — каждая смена на отдельном листе (имя «ДД.ММ.ГГ день/ночь
 *   Линия»): инфо-строка «Дата» | дата (день/ночь) | «Мастер» | мастер |
 *   «Принял» (значение пустое — под роспись); жирным в шапке выделено всё,
 *   кроме «Принял» и «Линии». Таблица: Линия | N парт | продукт | тара |
 *   количество | факт (пусто) | карантин (пусто) | Примечание (пусто);
 *   сортировка по номеру партии (числом). Поля страницы: слева 1см,
 *   сверху/справа/снизу 0,5см.
 *
 * Ширины колонок и высоты строк подбираются автоматически по содержимому
 * (autoFitSheet: ширина — по самому длинному тексту колонки, высота — по
 * числу строк текста в колонках с автопереносом). Колонки «количество»,
 * «факт», «карантин» — одной ширины: по самой широкой из трёх (бланк смены
 * и журнал по сменам).
 *
 * Колонтитулы отключены во всех выгрузках.
 *
 * Стиль — по образцу thermizationXlsx.ts (тонкие границы, bold-шапка, заливка)
 * и серверного экспорта факта (bgcolor FF72BF44); шрифт всех выгрузок — 24pt.
 */

/** Строка бланка — данные одной записи факта смены */
export interface ShiftFactExportRow {
  /** № партии — колонка «номер партии» */
  batch_number?: string | number | null;
  /** Наименование продукта */
  product_name?: string | null;
  /** Тара */
  container_name?: string | null;
  /** Кол-во выпущенного продукта, шт — зарегистрированный факт */
  quantity?: string | number | null;
}

/** Документ для выгрузки бланка факта (вкладка факта смены) */
export interface ShiftFactExportDoc {
  /** Тип вкладки, используется в имени файла: «факт_розлива», «факт_гот_продукции», «факт_полуфабриката» */
  kind: string;
  /** Дата смены (ISO) */
  shift_date?: string | null;
  /** Время суток смены: 'day' | 'night' */
  times_of_day?: string | null;
  /** Мастер смены */
  master_name?: string | null;
  /** Шаблон линии смены: 'atom' | 'vdp' | 'custom' | 'wash' */
  template?: string | null;
  rows: ShiftFactExportRow[];
}

/** Строка журнала из списка смен — факт одной партии выбранной смены */
export interface ShiftFactListExportRow {
  /** Дата смены (ISO) */
  shift_date?: string | null;
  /** Время суток смены: 'day' | 'night' */
  times_of_day?: string | null;
  /** Шаблон линии смены: 'atom' | 'vdp' | 'custom' | 'wash' */
  template?: string | null;
  /** Мастер смены */
  master_name?: string | null;
  /** № партии */
  batch_number?: string | number | null;
  /** Наименование продукта */
  product_name?: string | null;
  /** Тара */
  container_name?: string | null;
  /** Кол-во выпущенного продукта, шт — зарегистрированный факт */
  quantity?: string | number | null;
}

const HEADER_LABELS = [
  "Линия",
  "N парт",
  "продукт",
  "тара",
  "количество",
  "факт",
  "карантин",
  "Примечание",
];

const LIST_HEADER_LABELS = [
  "Линия",
  "дата",
  "смена д/н",
  "мастер",
  "N парт",
  "продукт",
  "тара",
  "количество",
];

/** Подписи шаблонов линий (enum shifts.templates) */
const LINE_TEMPLATE_LABELS: Record<string, string> = {
  atom: "АТОМ",
  vdp: "ВДП",
  custom: "Общая",
  wash: "Мойка",
};

/** Значение enum шаблона линии → подпись для колонки «Линия» */
export function templateLabel(value?: string | null): string {
  if (!value) return "";
  return LINE_TEMPLATE_LABELS[value] || String(value);
}

/**
 * Поля страницы: сверху/справа/снизу 0,5см, колонтитулов нет; левое поле —
 * параметр (бланк смены и общий журнал — 2см ≈ 0,79; журнал по сменам —
 * 1см ≈ 0,39). ExcelJS принимает поля в дюймах.
 */
function applyPrintLayout(sheet: ExcelJS.Worksheet, leftInches = 0.79) {
  sheet.pageSetup.margins = {
    left: leftInches,
    right: 0.2,
    top: 0.2,
    bottom: 0.2,
    header: 0,
    footer: 0,
  };
  sheet.headerFooter = { oddHeader: "", oddFooter: "" };
}

/** Шрифт всех выгрузок бланка факта (бланк отдельной смены и списочная) — 24pt */
const FONT_SIZE = 24;

/** Оценочная ширина одного символа шрифта 24pt в единицах ширины колонок Excel (кириллица) */
const CHAR_UNIT = 3;
/** Границы автоподбора ширины колонки */
const MIN_COL_WIDTH = 10;
const MAX_COL_WIDTH = 55;
/** Высота одной строки текста 24pt, pt */
const LINE_HEIGHT = 32;
/** Вертикальный запас высоты строки, pt */
const ROW_PADDING = 14;

/** Колонки одной ширины — по самой широкой из группы (обе выгрузки факта) */
const EQUAL_WIDTH_LABELS = ["количество", "факт", "карантин"];

/** 1-based номера колонок группы равных ширин по списку подписей шапки */
function equalWidthColumnIndexes(labels: string[]): number[] {
  return EQUAL_WIDTH_LABELS.map((label) => labels.indexOf(label) + 1).filter((idx) => idx > 0);
}

/**
 * Автоподбор размеров листа: ширина каждой колонки — по самому длинному тексту
 * в её ячейках (строки widthFromRow..lastRow), высота строк heightFromRow..lastRow —
 * по числу строк текста во всех ячейках строки с включённым автопереносом
 * (wrapText), включая шапку. ExcelJS не умеет автоподбор сам, поэтому ширина
 * оценивается по длине текста с коэффициентом шрифта 24pt.
 * Колонки из equalWidthColumns получают одну ширину — по самой широкой из них.
 */
function autoFitSheet(
  sheet: ExcelJS.Worksheet,
  colCount: number,
  lastRow: number,
  opts: { widthFromRow: number; heightFromRow: number; equalWidthColumns?: number[] },
) {
  for (let c = 1; c <= colCount; c++) {
    let maxLen = 0;
    for (let r = opts.widthFromRow; r <= lastRow; r++) {
      const value = sheet.getRow(r).getCell(c).value;
      if (value === null || value === undefined || typeof value === "object") continue;
      maxLen = Math.max(maxLen, String(value).length);
    }
    sheet.getColumn(c).width = Math.min(
      Math.max(maxLen * CHAR_UNIT, MIN_COL_WIDTH),
      MAX_COL_WIDTH,
    );
  }

  // Равные ширины — до расчёта высот: высота строки зависит от ширины колонок
  if (opts.equalWidthColumns?.length) {
    const groupWidth = Math.max(
      ...opts.equalWidthColumns.map((c) => Number(sheet.getColumn(c).width) || MIN_COL_WIDTH),
    );
    for (const c of opts.equalWidthColumns) {
      sheet.getColumn(c).width = groupWidth;
    }
  }

  for (let r = opts.heightFromRow; r <= lastRow; r++) {
    let lines = 1;
    for (let c = 1; c <= colCount; c++) {
      const cell = sheet.getRow(r).getCell(c);
      const value = cell.value;
      if (value === null || value === undefined || typeof value === "object") continue;
      if (!cell.alignment?.wrapText) continue;
      const width = Number(sheet.getColumn(c).width) || MIN_COL_WIDTH;
      const charsPerLine = Math.max(1, Math.floor(width / CHAR_UNIT));
      lines = Math.max(lines, Math.ceil(String(value).length / charsPerLine));
    }
    sheet.getRow(r).height = lines * LINE_HEIGHT + ROW_PADDING;
  }
}

/** Заливка шапки таблицы — как в серверном XLSX-экспорте факта (зелёный) */
const HEADER_FILL_ARGB = "FF72BF44";
const BORDER_GRAY_ARGB = "FF808080";

const THIN_BORDER = {
  top: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
  left: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
  bottom: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
  right: { style: "thin" as const, color: { argb: BORDER_GRAY_ARGB } },
};

/** Первая строка бланка: «Дата», дата, день/ночь, «Мастер», мастер */
const HEADER_ROW_INDEX = 3;

function formatDate(value?: string | null, format = "DD.MM.YYYY"): string {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(format) : String(value);
}

/** 'day' → «день», 'night' → «ночь», прочее — как есть */
function formatTimesOfDay(value?: string | null): string {
  if (!value) return "";
  if (value === "day") return "день";
  if (value === "night") return "ночь";
  return String(value);
}

function toNumberCell(value?: string | number | null): number | string {
  if (value === null || value === undefined || value === "") return "";
  const parsed = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : String(value);
}

function applyFont(cell: ExcelJS.Cell, bold = false, size: number = FONT_SIZE) {
  cell.font = { size, bold };
}

/**
 * Сравнение номеров партий: числовой порядок ('2' раньше '10');
 * нечисловые номера («Д5», «Д10») — строковое сравнение с числовым порядком.
 */
function compareBatchNumbers(
  a?: string | number | null,
  b?: string | number | null,
): number {
  const an = parseFloat(String(a ?? ""));
  const bn = parseFloat(String(b ?? ""));
  if (Number.isFinite(an) && Number.isFinite(bn) && an !== bn) return an - bn;

  return String(a ?? "").localeCompare(String(b ?? ""), "ru", {
    numeric: true,
  });
}

/** Порядок времени суток в сортировке списочной выгрузки: день, затем ночь, прочее — в конце */
function timesOfDayOrder(value?: string | null): number {
  if (value === "day") return 0;
  if (value === "night") return 1;
  return 2;
}

/**
 * Сортировка строк общего журнала: сначала по дате смены, внутри даты —
 * по линии (по алфавиту подписи: АТОМ, Общая), затем по времени суток
 * (сначала день, потом ночь), внутри смены — по номеру партии (числовой
 * порядок; нечисловые номера — строковое сравнение).
 */
export function compareShiftFactListRows(
  a: ShiftFactListExportRow,
  b: ShiftFactListExportRow,
): number {
  const timeOf = (row: ShiftFactListExportRow) => {
    const value = row.shift_date ? moment(row.shift_date).valueOf() : NaN;
    return Number.isFinite(value) ? value : 0;
  };

  const dateDiff = timeOf(a) - timeOf(b);
  if (dateDiff !== 0) return dateDiff;

  const lineDiff = templateLabel(a.template).localeCompare(
    templateLabel(b.template),
    "ru",
    { numeric: true },
  );
  if (lineDiff !== 0) return lineDiff;

  const todDiff = timesOfDayOrder(a.times_of_day) - timesOfDayOrder(b.times_of_day);
  if (todDiff !== 0) return todDiff;

  return compareBatchNumbers(a.batch_number, b.batch_number);
}

/** Выравнивание по середине ячейки (вертикаль) */
function applyMiddleAlign(
  cell: ExcelJS.Cell,
  horizontal: "left" | "center" = "left",
  wrap = true,
) {
  cell.alignment = { horizontal, vertical: "middle", wrapText: wrap };
}

/** Опции листа факта одной смены (инфо-строка + таблица) */
interface FactSheetOptions {
  /** Левое поле листа, дюймы */
  leftMarginIn: number;
  /** Инфо-строка с «Принял» (значение пустое — под роспись) */
  withAcceptedBy: boolean;
  /** Жирным выделена вся инфо-строка, кроме «Принял» */
  boldInfoValues: boolean;
  /** В шапке таблицы не выделять жирным «Линию» */
  headerBoldExceptLine: boolean;
}

/** Лист бланка факта отдельной смены (из карточки смены) */
const BLANK_SHEET_OPTIONS: FactSheetOptions = {
  leftMarginIn: 0.79,
  withAcceptedBy: false,
  boldInfoValues: false,
  headerBoldExceptLine: false,
};

/** Лист журнала по сменам: «Принял», жирная шапка кроме «Принял»/«Линии» */
const JOURNAL_SHEET_OPTIONS: FactSheetOptions = {
  leftMarginIn: 0.39,
  withAcceptedBy: true,
  boldInfoValues: true,
  headerBoldExceptLine: true,
};

/**
 * Лист факта одной смены: инфо-строка «Дата» | дата (день-ночь, объединены
 * 2:3) | «Мастер» | мастер [| «Принял» | пусто], пустая строка, зелёная шапка
 * таблицы и строки данных, отсортированные по номеру партии (числом);
 * «факт», «карантин», «Примечание» — пустые, заполняются вручную.
 * Все ячейки — по вертикали по середине; инфо-строка без автопереноса —
 * текст влезает благодаря автоподбору ширины колонок.
 */
function buildFactSheet(
  sheet: ExcelJS.Worksheet,
  doc: ShiftFactExportDoc,
  opts: FactSheetOptions,
) {
  applyPrintLayout(sheet, opts.leftMarginIn);

  // Строка 1: Дата / дата + день-ночь / Мастер / мастер [/ Принял / пусто]
  const infoRow = sheet.getRow(1);
  infoRow.getCell(1).value = "Дата";

  const dateText = formatDate(doc.shift_date);
  const dayNightText = formatTimesOfDay(doc.times_of_day);
  infoRow.getCell(2).value = dayNightText ? `${dateText} (${dayNightText})` : dateText;
  // Дата и день/ночь — в одной объединённой ячейке 2:3
  sheet.mergeCells(1, 2, 1, 3);

  infoRow.getCell(4).value = "Мастер";
  infoRow.getCell(5).value = doc.master_name || "";

  const infoCellCount = opts.withAcceptedBy ? 7 : 5;
  if (opts.withAcceptedBy) {
    infoRow.getCell(6).value = "Принял";
    infoRow.getCell(7).value = "";
  }

  for (let c = 1; c <= infoCellCount; c++) {
    const isLabel = c === 1 || c === 4 || c === 6;
    // Журнал по сменам: жирным вся строка, кроме «Принял» (и её пустого
    // значения); бланк: только подписи «Дата» и «Мастер»
    const bold = opts.boldInfoValues ? c !== 6 && c !== 7 : c === 1 || c === 4;
    applyFont(infoRow.getCell(c), bold);
    applyMiddleAlign(infoRow.getCell(c), isLabel ? "center" : "left", false);
  }

  // Строка 2 — пустая (разделитель)

  // Строка 3: шапка таблицы («Линия» — без жирного в журнале по сменам)
  const headerRow = sheet.getRow(HEADER_ROW_INDEX);
  headerRow.values = HEADER_LABELS;
  headerRow.eachCell((cell, colNumber) => {
    applyFont(cell, !(opts.headerBoldExceptLine && colNumber === 1));
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = THIN_BORDER;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL_ARGB },
    };
  });

  // Строки данных: линия, N парт, продукт, тара, количество;
  // сортировка по номеру партии (числом);
  // «факт», «карантин», «Примечание» — пустые, заполняются вручную
  const sortedRows = [...(doc.rows || [])].sort((a, b) =>
    compareBatchNumbers(a.batch_number, b.batch_number),
  );
  const lastRow = HEADER_ROW_INDEX + sortedRows.length;
  sortedRows.forEach((row, idx) => {
    const r = HEADER_ROW_INDEX + 1 + idx;
    const dataRow = sheet.getRow(r);
    dataRow.getCell(1).value = templateLabel(doc.template);
    dataRow.getCell(2).value = row.batch_number != null ? row.batch_number : "";
    dataRow.getCell(3).value = row.product_name || "";
    dataRow.getCell(4).value = row.container_name || "";
    dataRow.getCell(5).value = toNumberCell(row.quantity);
    for (let c = 1; c <= 8; c++) {
      const cell = dataRow.getCell(c);
      applyFont(cell);
      cell.border = THIN_BORDER;
      cell.alignment = {
        horizontal: c === 3 || c === 8 ? "left" : "center",
        vertical: "middle",
        wrapText: c === 3 || c === 8,
      };
    }
  });

  // Автоподбор: ширины считаем от шапки таблицы (строка 1 с объединённой
  // ячейкой даты не участвует), высоты — всей таблицы; «количество»,
  // «факт», «карантин» — одной ширины
  autoFitSheet(sheet, HEADER_LABELS.length, lastRow, {
    widthFromRow: HEADER_ROW_INDEX,
    heightFromRow: HEADER_ROW_INDEX,
    equalWidthColumns: equalWidthColumnIndexes(HEADER_LABELS),
  });

  // Объединённая ячейка даты (2:3) должна вмещать свою строку целиком:
  // при нехватки суммарной ширины колонок 2+3 добавляем deficit колонке 3
  const infoDateText = String(infoRow.getCell(2).value || "");
  const mergedWidth = Number(sheet.getColumn(2).width) + Number(sheet.getColumn(3).width);
  const neededWidth = infoDateText.length * CHAR_UNIT + 2;
  if (mergedWidth < neededWidth) {
    const dateTailColumn = sheet.getColumn(3);
    dateTailColumn.width = (dateTailColumn.width ?? MIN_COL_WIDTH) + (neededWidth - mergedWidth);
  }
}

/**
 * Собирает workbook бланка факта (синхронно, без записи буфера) —
 * используется и компонентами, и юнит-тестами.
 */
export function createShiftFactWorkbook(doc: ShiftFactExportDoc): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  const sheet = wb.addWorksheet("Факт", {
    pageSetup: { fitToPage: true },
  });
  buildFactSheet(sheet, doc, BLANK_SHEET_OPTIONS);
  return wb;
}

/**
 * Общий журнал из списка смен: все смены на одном листе, без инфо-строки —
 * сразу зелёная шапка таблицы с названиями колонок и строки данных
 * (линия, дата ДД.ММ.ГГ, смена д/н, мастер, N парт, продукт, тара,
 * количество).
 *
 * Строки сортируются по дате смены, внутри даты — по линии, затем смена
 * д/н (сначала день, потом ночь), затем по номеру партии (числом);
 * шрифт — 24pt; ширины колонок и высоты строк подбираются автоматически
 * по содержимому.
 */
export function createShiftFactListWorkbook(rows: ShiftFactListExportRow[]): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  const sheet = wb.addWorksheet("Факт", {
    pageSetup: { fitToPage: true },
  });
  applyPrintLayout(sheet);

  // Строка 1: шапка таблицы с названиями колонок
  const headerRow = sheet.getRow(1);
  headerRow.values = LIST_HEADER_LABELS;
  headerRow.eachCell((cell) => {
    applyFont(cell, true);
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = THIN_BORDER;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL_ARGB },
    };
  });

  const DATA_START_ROW = 2;
  const sortedRows = [...(rows || [])].sort(compareShiftFactListRows);
  sortedRows.forEach((row, idx) => {
    const dataRow = sheet.getRow(DATA_START_ROW + idx);
    dataRow.getCell(1).value = templateLabel(row.template);
    dataRow.getCell(2).value = formatDate(row.shift_date, "DD.MM.YY");
    dataRow.getCell(3).value = formatTimesOfDay(row.times_of_day);
    dataRow.getCell(4).value = row.master_name || "";
    dataRow.getCell(5).value = row.batch_number != null ? row.batch_number : "";
    dataRow.getCell(6).value = row.product_name || "";
    dataRow.getCell(7).value = row.container_name || "";
    dataRow.getCell(8).value = toNumberCell(row.quantity);
    for (let c = 1; c <= 8; c++) {
      const cell = dataRow.getCell(c);
      applyFont(cell, false);
      cell.border = THIN_BORDER;
      cell.alignment = {
        horizontal: c === 4 || c === 6 ? "left" : "center",
        vertical: "middle",
        wrapText: c === 4 || c === 6,
      };
    }
  });

  // Автоподбор по всей таблице; перенос — «мастер», «продукт»
  const lastRow = DATA_START_ROW + sortedRows.length - 1;
  autoFitSheet(sheet, LIST_HEADER_LABELS.length, Math.max(lastRow, 1), {
    widthFromRow: 1,
    heightFromRow: 1,
    equalWidthColumns: equalWidthColumnIndexes(LIST_HEADER_LABELS),
  });

  return wb;
}

/** Имя листа журнала по сменам: «ДД.ММ.ГГ день/ночь Линия» */
function journalSheetName(doc: ShiftFactExportDoc): string {
  const parts = [
    formatDate(doc.shift_date, "DD.MM.YY"),
    formatTimesOfDay(doc.times_of_day),
    templateLabel(doc.template),
  ].filter(Boolean);
  return parts.join(" ") || "Смена";
}

/**
 * Журнал по сменам из списка смен: каждая смена — отдельный лист
 * («ДД.ММ.ГГ день/ночь Линия», повторы — с номером «(2)»). Лист — как бланк
 * факта отдельной смены, но с колонкой «Принял» в инфо-строке (значение
 * пустое — под роспись), жирной шапкой везде, кроме «Принял» и «Линии», и
 * левым полем листа 1см. Строки каждого листа сортируются по номеру партии;
 * листы идут в порядке общего журнала (дата → линия → смена д/н).
 */
export function createShiftFactJournalWorkbook(rows: ShiftFactListExportRow[]): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  const sortedRows = [...(rows || [])].sort(compareShiftFactListRows);

  // Группировка по смене: дата + время суток + шаблон линии + мастер
  const docs: ShiftFactExportDoc[] = [];
  const docByKey = new Map<string, ShiftFactExportDoc>();
  for (const row of sortedRows) {
    const key = [
      row.shift_date ?? "",
      row.times_of_day ?? "",
      row.template ?? "",
      row.master_name ?? "",
    ].join("\n");
    let doc = docByKey.get(key);
    if (!doc) {
      doc = {
        kind: "журнал",
        shift_date: row.shift_date,
        times_of_day: row.times_of_day,
        template: row.template,
        master_name: row.master_name,
        rows: [],
      };
      docByKey.set(key, doc);
      docs.push(doc);
    }
    doc.rows.push({
      batch_number: row.batch_number,
      product_name: row.product_name,
      container_name: row.container_name,
      quantity: row.quantity,
    });
  }

  const usedNames = new Set<string>();
  for (const doc of docs) {
    const base = journalSheetName(doc);
    let name = base;
    let copy = 2;
    while (usedNames.has(name)) {
      name = `${base} (${copy++})`;
    }
    usedNames.add(name);

    const sheet = wb.addWorksheet(name, {
      pageSetup: { fitToPage: true },
    });
    buildFactSheet(sheet, doc, JOURNAL_SHEET_OPTIONS);
  }

  return wb;
}

/** Имя файла бланка: <kind>_<дата смены YYYY-MM-DD>.xlsx */
export function shiftFactFilename(doc: ShiftFactExportDoc): string {
  const date = doc.shift_date && moment(doc.shift_date).isValid()
    ? moment(doc.shift_date).format("YYYY-MM-DD")
    : moment().format("YYYY-MM-DD");
  return `${doc.kind}_${date}.xlsx`;
}

/** Имя файла общего журнала: журнал_общий_<текущая дата>.xlsx */
export function shiftFactListFilename(): string {
  return `журнал_общий_${moment().format("YYYY-MM-DD")}.xlsx`;
}

/** Имя файла журнала по сменам: журнал_по_сменам_<текущая дата>.xlsx */
export function shiftFactJournalFilename(): string {
  return `журнал_по_сменам_${moment().format("YYYY-MM-DD")}.xlsx`;
}

/** Собирает workbook основной выгрузки и возвращает XLSX-буфер для скачивания */
export async function buildShiftFactWorkbook(doc: ShiftFactExportDoc): Promise<ArrayBuffer> {
  try {
    const wb = createShiftFactWorkbook(doc);
    return await wb.xlsx.writeBuffer();
  } catch (err) {
    console.error("[shiftFactXlsx] writeBuffer error", err);
    throw err;
  }
}

/** Собирает workbook общего журнала и возвращает XLSX-буфер для скачивания */
export async function buildShiftFactListWorkbook(
  rows: ShiftFactListExportRow[],
): Promise<ArrayBuffer> {
  try {
    const wb = createShiftFactListWorkbook(rows);
    return await wb.xlsx.writeBuffer();
  } catch (err) {
    console.error("[shiftFactXlsx] list writeBuffer error", err);
    throw err;
  }
}

/** Собирает workbook журнала по сменам и возвращает XLSX-буфер для скачивания */
export async function buildShiftFactJournalWorkbook(
  rows: ShiftFactListExportRow[],
): Promise<ArrayBuffer> {
  try {
    const wb = createShiftFactJournalWorkbook(rows);
    return await wb.xlsx.writeBuffer();
  } catch (err) {
    console.error("[shiftFactXlsx] journal writeBuffer error", err);
    throw err;
  }
}
